import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Loader2, ExternalLink, ClipboardList, Clock, Calendar, Trophy, PlayCircle, X, CheckCircle2, RotateCcw } from 'lucide-react';
import {
  useModuleDetailsQuery,
  useModuleCompletionQuery,
  useMarkModuleCompleteMutation,
  CourseModule,
} from '../../../api/studentCoursesApi';
import { tokenService } from '../../../services/TokenService';
import { useQuizAttemptHistory } from '../../../api/quizAttemptApi';
import QuizContainer from './QuizContainer';

interface QuizModuleContentProps {
  contextId: string;
  moduleId: string;
  module: CourseModule | null;
  headerColor?: string;
}

const QuizModuleContent: React.FC<QuizModuleContentProps> = ({ contextId, moduleId, module, headerColor }) => {
  const token = tokenService.getToken();
  const [showQuizFrame, setShowQuizFrame] = useState(false);

  const { data: completionData, refetch: refetchCompletion } =
    useModuleCompletionQuery(contextId, moduleId, token);
  const markComplete = useMarkModuleCompleteMutation(contextId);
  const isCompleted = completionData?.completed ?? false;

  const handleQuizComplete = () => {
    // Try refetch first (automatic completion mode), then push manually
    refetchCompletion().then(({ data }) => {
      if (!data?.completed) {
        markComplete.mutate(
          { moduleId },
          { onSuccess: () => refetchCompletion(), onError: () => {} },
        );
      }
    });
    refetchHistory();
  };

  const { data: quizDetails, isLoading, error } = useModuleDetailsQuery(
    contextId,
    moduleId,
    'quiz',
    token,
    {
      enabled: !!contextId && !!moduleId,
    }
  );

  const { data: historyData, refetch: refetchHistory } = useQuizAttemptHistory(
    contextId,
    quizDetails?.id
  );
  const pastAttempts = historyData?.data?.attempts ?? [];

  const handleOpenInMoodle = () => {
    if (module?.url) {
      window.open(module.url, '_blank');
    }
  };

  const handleStartQuiz = () => {
    setShowQuizFrame(true);
  };

  const handleCloseQuiz = () => {
    setShowQuizFrame(false);
  };

  const formatTime = (seconds: number | undefined): string => {
    if (!seconds) return 'No limit';
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${minutes}m`;
  };

  const formatDate = (timestamp: string | null | undefined): string => {
    if (!timestamp) return 'Not set';
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12">
        <div className="flex flex-col items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-3" />
          <span className="text-gray-600">Loading quiz details...</span>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-red-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800 mb-1">
              Failed to load quiz details
            </h3>
            <p className="text-sm text-red-700">
              {error instanceof Error ? error.message : 'An unknown error occurred'}
            </p>
            {module?.url && (
              <Button
                onClick={handleOpenInMoodle}
                variant="outline"
                size="sm"
                className="mt-3"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in Moodle
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Quiz Frame View (when taking quiz)
  if (showQuizFrame && module?.url) {
    if (quizDetails?.id) {
      return (
        <QuizContainer
          contextId={contextId}
          quizId={quizDetails.id}
          quizName={quizDetails.name || module.name}
          maxGrade={quizDetails.grade}
          attemptsAllowed={quizDetails.attempts}
          onClose={handleCloseQuiz}
          onComplete={handleQuizComplete}
          headerColor={headerColor}
        />
      );
    }

    // Fallback to iframe
    return (
      <div className="fixed inset-0 z-50 bg-white">
        <div className="sticky top-0 z-10 border-b border-gray-200" style={{ background: '#F9FAFB', borderTop: `4px solid ${headerColor || '#374151'}` }}>
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg p-2 shrink-0" style={{ background: headerColor ? `${headerColor}1A` : '#E5E7EB' }}>
                <ClipboardList className="h-5 w-5" style={{ color: headerColor || '#374151' }} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Quiz</p>
                <h1 className="text-base font-bold text-gray-900">{quizDetails?.name || module?.name}</h1>
              </div>
            </div>
            <Button onClick={handleCloseQuiz} variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        <iframe
          src={module.url}
          className="w-full h-[calc(100vh-56px)] border-0"
          title="Quiz"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-top-navigation-by-user-activation allow-downloads"
          allow="fullscreen; clipboard-read; clipboard-write"
        />
      </div>
    );
  }

  // Quiz Details Content
  if (quizDetails) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* ── Header ── */}
        <div className="px-6 py-5 overflow-hidden" style={{ background: '#F9FAFB', borderTop: `4px solid ${headerColor || '#374151'}` }}>
          <div className="flex items-start gap-4">
            <div className="rounded-lg p-2.5 shrink-0" style={{ background: headerColor ? `${headerColor}1A` : '#E5E7EB' }}>
              <ClipboardList className="h-6 w-6" style={{ color: headerColor || '#374151' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-1">Quiz</p>
              <h2 className="text-xl font-bold leading-snug text-gray-900">{quizDetails.name || module?.name}</h2>
              {quizDetails.intro && (
                <div
                  className="mt-2 text-sm text-gray-500 line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: quizDetails.intro }}
                />
              )}
            </div>
          </div>
        </div>

        {/* ── Info table ── */}
        <div className="max-h-[calc(100vh-14rem)] overflow-y-auto">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              {quizDetails.timeLimit !== undefined && (
                <tr>
                  <td className="px-6 py-3 w-44">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <Clock className="h-3.5 w-3.5 text-blue-400" />
                      Time Limit
                    </div>
                  </td>
                  <td className="px-6 py-3 text-gray-800 font-medium">{formatTime(quizDetails.timeLimit)}</td>
                </tr>
              )}
              {quizDetails.attempts !== undefined && (
                <tr>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <ClipboardList className="h-3.5 w-3.5 text-purple-400" />
                      Attempts
                    </div>
                  </td>
                  <td className="px-6 py-3 text-gray-800 font-medium">
                    {quizDetails.attempts === 0 ? 'Unlimited' : quizDetails.attempts}
                  </td>
                </tr>
              )}
              {quizDetails.grade !== undefined && (
                <tr>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <Trophy className="h-3.5 w-3.5 text-yellow-400" />
                      Max Grade
                    </div>
                  </td>
                  <td className="px-6 py-3 text-gray-800 font-medium">{quizDetails.grade} points</td>
                </tr>
              )}
              {quizDetails.timeOpen && (
                <tr>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <Calendar className="h-3.5 w-3.5 text-green-400" />
                      Opens
                    </div>
                  </td>
                  <td className="px-6 py-3 text-gray-800">{formatDate(quizDetails.timeOpen)}</td>
                </tr>
              )}
              {quizDetails.timeClose && (
                <tr>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <Calendar className="h-3.5 w-3.5 text-red-400" />
                      Closes
                    </div>
                  </td>
                  <td className="px-6 py-3 text-gray-800">{formatDate(quizDetails.timeClose)}</td>
                </tr>
              )}
              {quizDetails.shuffleAnswers && (
                <tr>
                  <td className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Options</td>
                  <td className="px-6 py-3 text-gray-600">Answers will be shuffled</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Attempt History ── */}
        {pastAttempts.length > 0 && (() => {
          const maxGrade = quizDetails.grade ?? 0;
          const bestScore = Math.max(...pastAttempts.map(a => a.sumgrades ?? 0));
          const sorted = [...pastAttempts].reverse();
          // Split into 3 columns
          const col1 = sorted.filter((_, i) => i % 3 === 0);
          const col2 = sorted.filter((_, i) => i % 3 === 1);
          const col3 = sorted.filter((_, i) => i % 3 === 2);

          const renderAttempt = (attempt: typeof pastAttempts[0]) => {
            const pct = maxGrade > 0 && attempt.sumgrades !== null
              ? Math.round(((attempt.sumgrades ?? 0) / maxGrade) * 100)
              : null;
            const duration = attempt.timefinish && attempt.timestart
              ? attempt.timefinish - attempt.timestart
              : null;
            const durationStr = duration
              ? duration < 60 ? `${duration}s` : `${Math.floor(duration / 60)}m ${duration % 60}s`
              : null;
            const isPerfect = pct === 100;
            const isFailed = pct !== null && pct < 50;
            return (
              <div
                key={attempt.id}
                className={`rounded-lg border px-3.5 py-3 text-sm ${
                  isPerfect ? 'bg-green-50 border-green-200' :
                  isFailed ? 'bg-red-50 border-red-100' :
                  'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">
                    Attempt {attempt.attempt}
                  </span>
                  {pct !== null && (
                    <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${
                      isPerfect ? 'bg-green-100 text-green-700' :
                      isFailed ? 'bg-red-100 text-red-600' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {pct}%
                    </span>
                  )}
                </div>
                <div className={`font-semibold ${isPerfect ? 'text-green-700' : isFailed ? 'text-red-600' : 'text-gray-800'}`}>
                  {attempt.sumgrades !== null && attempt.sumgrades !== undefined
                    ? `${attempt.sumgrades} / ${maxGrade > 0 ? maxGrade : '?'}`
                    : '—'} marks
                </div>
                {attempt.timefinish ? (
                  <div className="text-[11px] text-gray-400 mt-0.5 leading-tight">
                    {new Date(attempt.timefinish * 1000).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {durationStr && <span className="ml-1.5">· {durationStr}</span>}
                  </div>
                ) : null}
              </div>
            );
          };

          return (
            <div className="border-t border-gray-100">
              <div className="px-6 py-3 bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <RotateCcw className="h-3.5 w-3.5 text-blue-400" />
                  Your Attempts
                </div>
                {maxGrade > 0 && (
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-full px-2.5 py-0.5">
                    <Trophy className="h-3 w-3" />
                    Best: {bestScore} / {maxGrade}
                  </div>
                )}
              </div>
              <div className="px-4 pb-4 pt-3 grid grid-cols-3 gap-2">
                <div className="flex flex-col gap-2">{col1.map(renderAttempt)}</div>
                <div className="flex flex-col gap-2">{col2.map(renderAttempt)}</div>
                <div className="flex flex-col gap-2">{col3.map(renderAttempt)}</div>
              </div>
            </div>
          );
        })()}

        {/* ── Footer ── */}
        <div className="sticky bottom-0 z-10 border-t border-gray-100 px-6 py-4 bg-gray-50 flex items-center justify-between gap-3">
          {isCompleted ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full shadow-sm">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
              <span className="text-xs font-semibold text-green-700">Completed</span>
            </div>
          ) : <div />}
          {module?.url && (
            <Button onClick={handleStartQuiz} className="bg-blue-600 hover:bg-blue-700" size="sm">
              <PlayCircle className="h-4 w-4 mr-1.5" />
              Start Quiz
            </Button>
          )}
        </div>
      </div>
    );
  }

  // No Content State
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-12">
      <div className="text-center">
        <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 mb-4">No quiz details available</p>
        {module?.url && (
          <Button onClick={handleOpenInMoodle} variant="outline">
            <ExternalLink className="h-4 w-4 mr-2" />
            Open in Moodle
          </Button>
        )}
      </div>
    </div>
  );
};

export default QuizModuleContent;
